import { useAppContext } from './../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../request'
import React from 'react'

export type TranslationLanguage = {
  name: string
  code: string
  translations: { [key: string]: string }
  extrasLoaded?: string[]
}

/**
 * useTranslation is a hook that returns the currently active language and a function to translate text.
 * Translations are loaded from the server when the language is changed, and the provided `tr` function is used to translate text.
 * @param extra - An optional string that specifies additional translations to load.
 * If provided, the translations will be loaded when the component mounts.
 * @returns An object containing:
 *   - `language`: The currently active language object, or `undefined` if no language is set.
 *   - `tr`: A function to translate text. If no translation is found, the original text is returned.
 *   - `changeLanguage`: A function to change the active language by its code.
 */
export const useTranslation = (extra?: string) => {
  const { state, setState, startLoading, stopLoading } = useAppContext()

  const loadExtraTranslations = (key: string) => {
    if (!state.language || state.language.extrasLoaded?.includes(key)) return
    startLoading('changeLanguage')
    makeRequest<{ [key: string]: string } | undefined>(`/frontend_api/getExtraTranslations/${state.language.code}`, {
      key,
    })
      .then((response) => {
        setState((s) => ({
          ...s,
          language: state.language && {
            ...state.language,
            translations: {
              ...state.language.translations,
              ...response,
            },
            extrasLoaded: [...(state.language.extrasLoaded || []), key],
          },
        }))
        stopLoading('changeLanguage')
      })
      .catch(showAxiosErrorAsToast)
  }

  React.useEffect(() => {
    if (extra && state.language) loadExtraTranslations(extra)
  }, [extra])

  return React.useMemo(
    () => ({
      language: state.language,
      tr: (text: string) => {
        if (!state.language) return text
        return state.language.translations[text] || text
      },
      changeLanguage: async (code?: string) => {
        startLoading('changeLanguage')
        const loadedExtras = state.language?.extrasLoaded || []
        try {
          const response = await makeRequest<TranslationLanguage | undefined>(
            `/frontend_api/getTranslations/${code || ''}`,
            { extras: loadedExtras }
          )
          setState((s) => ({ ...s, language: response && { ...response, extrasLoaded: loadedExtras } }))
        } catch (e) {
          showAxiosErrorAsToast(e)
        }
        stopLoading('changeLanguage')
      },
    }),
    [state.language]
  )
}
